"use client"

import { useState, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { X, Check, Link2, Copy } from "lucide-react"
import { Z } from "@/lib/z-index"
import { useAnimatedClose } from "@/lib/use-animated-close"

interface ShareLinkPopupProps {
  title: string
  url: string
  onClose: () => void
}

/**
 * ShareLinkPopup — 类似抖音/小红书的分享弹窗，展示分享文案 + 链接，支持一键复制。
 */
export function ShareLinkPopup({ title, url, onClose }: ShareLinkPopupProps) {
  const { closing, requestClose } = useAnimatedClose(onClose, 240)
  const [entered, setEntered] = useState(false)
  const [copied, setCopied] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(raf)
  }, [])

  useEffect(() => () => clearTimeout(timerRef.current), [])

  const shareText = `${title} — 用 AI 搞定，真的太爽了！我在 SafeClaw 社区挖到的宝藏，分享给你 👇\n${url}`

  const handleCopy = () => {
    navigator.clipboard.writeText(shareText).then(() => {
      setCopied(true)
      timerRef.current = setTimeout(() => setCopied(false), 3000)
    }).catch(() => {
      // fallback: select textarea for manual copy
    })
  }

  const panelStyle: React.CSSProperties = {
    opacity: closing ? 0 : entered ? 1 : 0,
    transform: closing
      ? "scale(0.96) translateY(8px)"
      : entered
        ? "scale(1) translateY(0)"
        : "scale(0.92) translateY(16px)",
    filter: closing ? "blur(4px)" : entered ? "blur(0)" : "blur(6px)",
    transition: "opacity 240ms, transform 240ms, filter 240ms",
    transitionTimingFunction: closing
      ? "cubic-bezier(0.4, 0, 1, 1)"
      : "cubic-bezier(0.16, 1, 0.3, 1)",
  }

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/30 transition-opacity duration-240 ${
          closing ? "opacity-0" : entered ? "opacity-100" : "opacity-0"
        }`}
        style={{ zIndex: Z.MODAL_NESTED }}
        onClick={requestClose}
        aria-hidden
      />

      {/* Popup */}
      <div
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{ zIndex: Z.MODAL_NESTED + 1, pointerEvents: "none" }}
      >
        <div
          className="w-full max-w-sm"
          style={{ ...panelStyle, pointerEvents: "auto" }}
        >
          <div className="bg-card border border-border/60 rounded-2xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-border/40">
              <div className="flex items-center gap-2">
                <Link2 className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold text-foreground">分享链接</span>
              </div>
              <button
                type="button"
                onClick={requestClose}
                className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Content */}
            <div className="px-5 py-4">
              <p className="text-xs text-muted-foreground mb-2">复制以下内容，发给好友或分享到社交平台</p>
              <div className="bg-secondary/50 border border-border/40 rounded-xl px-4 py-3 text-sm text-foreground leading-relaxed whitespace-pre-wrap break-all select-all">
                {shareText}
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 pb-5 flex flex-col gap-2">
              <button
                type="button"
                onClick={handleCopy}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                  copied
                    ? "bg-green-500/15 text-green-600 dark:text-green-400"
                    : "bg-primary text-primary-foreground hover:opacity-90 active:scale-[0.98]"
                }`}
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4" />
                    复制成功，快去分享吧
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    一键复制
                  </>
                )}
              </button>
              {copied && (
                <p className="text-center text-xs text-muted-foreground">
                  粘贴到微信、朋友圈或任意聊天窗口即可分享
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  )
}
